import { useEffect, useState } from "react"

import { Button, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { finalize } from "rxjs/operators"

import { PoolSelectionRerun, PoolSelectionRerunObservable } from "../../../api/Randoms/PoolSelectionActions"
import PoolStatisticsDetailsGetObservable from "../../../api/Randoms/PoolStatisticsDetailsGet"
import PoolSelectionRow from "../../../api/Randoms/RandomsModel/PoolSelectionRow"
import PoolStatisticsDetails from "../../../api/Randoms/RandomsModel/PoolStatisticsDetails"
import handleAPIError from "../../../utils/handleAPIError"
import ActionButton from "../../ActionButton"

interface PoolSelectionRerunModalProps {
  show?: boolean
  poolId: number
  companyId: number
  row: PoolSelectionRow
  onHide: () => void
  onRefresh: () => void
}

function PoolSelectionRerunModal(props: PoolSelectionRerunModalProps) {
  const { show, poolId, companyId, row, onHide, onRefresh } = props
  const [running, setRunning] = useState(false)
  const [statistic, setStatistic] = useState<PoolStatisticsDetails>({} as PoolStatisticsDetails)
  const [loadingStatistic, setLoadingStatistic] = useState(false)

  const [actualToSelectDrug, setActualToSelectDrug] = useState(0)
  const [actualToSelectAlcohol, setActualToSelectAlcohol] = useState(0)
  const [alternateDrug, setAlternateDrug] = useState(0)
  const [alternateAlcohol, setAlternateAlcohol] = useState(0)

  useEffect(() => {
    setLoadingStatistic(true)
    const subs = PoolStatisticsDetailsGetObservable(poolId, 0, companyId)
      .pipe(finalize(() => setLoadingStatistic(false)))
      .subscribe(
        (data) => {
          setStatistic(data)
          setActualToSelectDrug(data.DrugRequired)
          setActualToSelectAlcohol(data.AlcoholRequired)
        },
        (error) => handleAPIError(error, () => {})
      )
    return () => subs.unsubscribe()
  }, [poolId, companyId])

  const handleReRun = () => {
    setRunning(true)
    const poolSelection: PoolSelectionRerun = {
      PoolSelectionID: row.PoolSelectionID,
      RequiredDrug: actualToSelectDrug || 0,
      RequiredAlcohol: actualToSelectAlcohol || 0,
      AlternateDrug: alternateDrug || 0,
      AlternateAlcohol: alternateAlcohol || 0,
      ExtraDrug: 0,
      ExtraAlcohol: 0,
      AccountID: 0,
    }
    PoolSelectionRerunObservable(poolSelection)
      .pipe(finalize(() => setRunning(false)))
      .subscribe(
        (data) => {
          onHide()
          onRefresh()
          toast.success(data)
        },
        (error) => handleAPIError(error, () => {})
      )
  }

  const handleActualToSelectDrugChange = (value: string) => {
    setActualToSelectDrug(Number(value.replace(/\D/g, "")) || 0)
  }

  const handleActualToSelectAlcoholChange = (value: string) => {
    setActualToSelectAlcohol(Number(value.replace(/\D/g, "")) || 0)
  }

  const handleAlternateDrugChange = (value: string) => {
    setAlternateDrug(Number(value.replace(/\D/g, "")) || 0)
  }

  const handleAlternateAlcoholChange = (value: string) => {
    setAlternateAlcohol(Number(value.replace(/\D/g, "")) || 0)
  }

  const validateSizeValue = (value: number | undefined) => {
    return !value || (value >= 0 && (!statistic || value <= statistic.CurrentPoolSize))
  }

  const isActualToSelectDrugValid = () => validateSizeValue(actualToSelectDrug)

  const isAlternateDrugValid = () => validateSizeValue(alternateDrug)

  const isActualToSelectAlcoholValid = () => validateSizeValue(actualToSelectAlcohol)

  const isAlternateAlcoholValid = () => validateSizeValue(alternateAlcohol)

  return (
    <Modal show={show} onHide={onHide} size={statistic?.GenerateAlternates ? "xl" : "lg"} centered>
      <Modal.Header closeButton>
        <Modal.Title>TESTS REQUIRED FOR SELECTION {row.Period}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          {loadingStatistic ? (
            <Row>
              <Col className="text-center">
                <Spinner animation="border" variant="secondary" />
              </Col>
            </Row>
          ) : (
            <Row>
              <Col>
                <Form.Row>
                  <Col
                    lg={statistic?.GenerateAlternates ? 4 : 5}
                    xl={statistic?.GenerateAlternates ? 3 : 5}
                    md={12}
                    className="form-row form-group form-inline"
                  >
                    <div className="col-form-label col-4 justify-content-start">DRUG</div>
                    <div className="col-form-label col-4 justify-content-start">{statistic?.DrugRequired || 0}</div>
                  </Col>
                  <Col
                    lg={statistic?.GenerateAlternates ? 12 : 12}
                    xl={statistic?.GenerateAlternates ? 5 : 7}
                    md={12}
                    className="form-row form-group form-inline"
                  >
                    <Form.Label
                      htmlFor="actual_to_select_drug_id"
                      className="col-form-label col-5 justify-content-start justify-content-lg-center"
                    >
                      ACTUAL TO SELECT
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="actual_to_select_drug_id"
                      className="col-4"
                      value={actualToSelectDrug}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleActualToSelectDrugChange(e.target.value)
                      }
                      isInvalid={!isActualToSelectDrugValid()}
                      disabled={false}
                    />
                    <Form.Control.Feedback type="invalid">
                      Drug Actual To Select is greater than pool size
                    </Form.Control.Feedback>
                  </Col>
                  {statistic?.GenerateAlternates === true && (
                    <Col lg={4} xl={4} md={12} className="form-row form-group form-inline">
                      <Form.Label
                        htmlFor="alternate_actual_to_select_drug_id"
                        className="col-form-label col-6 justify-content-start justify-content-lg-center"
                      >
                        ALTERNATES
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="alternate_actual_to_select_drug_id"
                        className="col-4"
                        value={alternateDrug}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAlternateDrugChange(e.target.value)}
                        isInvalid={!isAlternateDrugValid()}
                        disabled={false}
                      />
                      <Form.Control.Feedback type="invalid">
                        Number of drug Alternates exceeds pool membership count
                      </Form.Control.Feedback>
                    </Col>
                  )}
                </Form.Row>
                <Form.Row>
                  <Col
                    lg={statistic?.GenerateAlternates ? 4 : 5}
                    xl={statistic?.GenerateAlternates ? 3 : 5}
                    md={12}
                    className="form-row form-group form-inline"
                  >
                    <div className="col-form-label col-4 justify-content-start">ALCOHOL</div>
                    <div className="col-form-label col-4 justify-content-start">{statistic?.AlcoholRequired || 0}</div>
                  </Col>
                  <Col
                    lg={statistic?.GenerateAlternates ? 12 : 14}
                    xl={statistic?.GenerateAlternates ? 5 : 7}
                    md={12}
                    className="form-row form-group form-inline"
                  >
                    <Form.Label
                      htmlFor="actual_to_select_alcohol_id"
                      className="col-form-label col-5 justify-content-start justify-content-lg-center"
                    >
                      ACTUAL TO SELECT
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="actual_to_select_alcohol_id"
                      className="col-4"
                      value={actualToSelectAlcohol}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleActualToSelectAlcoholChange(e.target.value)
                      }
                      isInvalid={!isActualToSelectAlcoholValid()}
                      disabled={false}
                    />
                    <Form.Control.Feedback type="invalid">
                      Drug Actual To Select is greater than pool size
                    </Form.Control.Feedback>
                  </Col>
                  {statistic?.GenerateAlternates === true && (
                    <Col lg={4} xl={4} md={12} className="form-row form-group form-inline">
                      <Form.Label
                        htmlFor="alternate_actual_to_select_alcohol_id"
                        className="col-form-label col-6 justify-content-start justify-content-lg-center"
                      >
                        ALTERNATES
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="alternate_actual_to_select_alcohol_id"
                        className="col-4"
                        value={alternateAlcohol}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleAlternateAlcoholChange(e.target.value)
                        }
                        isInvalid={!isAlternateAlcoholValid()}
                        disabled={false}
                      />
                      <Form.Control.Feedback type="invalid">
                        Number of alcohol Alternates exceeds pool membership count
                      </Form.Control.Feedback>
                    </Col>
                  )}
                </Form.Row>
              </Col>
            </Row>
          )}
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>Cancel</Button>
        <ActionButton
          onClick={handleReRun}
          spin={running}
          disabled={
            !isActualToSelectDrugValid() ||
            !isActualToSelectAlcoholValid() ||
            !isAlternateDrugValid() ||
            !isAlternateAlcoholValid()
          }
          disableOnSpin
        >
          Run
        </ActionButton>
      </Modal.Footer>
    </Modal>
  )
}

PoolSelectionRerunModal.defaultProps = {
  show: true,
}
export default PoolSelectionRerunModal
